import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Link2, Copy, ExternalLink, CalendarClock, Save, CalendarOff, Plus, Trash2, CalendarPlus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DAYS = [
  { k: "monday", l: "Lun" },
  { k: "tuesday", l: "Mar" },
  { k: "wednesday", l: "Mié" },
  { k: "thursday", l: "Jue" },
  { k: "friday", l: "Vie" },
  { k: "saturday", l: "Sáb" },
  { k: "sunday", l: "Dom" },
];
const DEFAULT_WH = () => Object.fromEntries(DAYS.map((d) => [d.k, { enabled: ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(d.k), start: "09:00", end: "18:00", break_start: "13:00", break_end: "15:00" }]));

function SlugEditor({ user, setUser, t }) {
  const [slug, setSlug] = useState(user?.slug || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSlug(user?.slug || ""); }, [user?.slug]);
  const normalized = slug.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  const publicBase = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
  const previewUrl = `${publicBase}/reservation/${normalized || "..."}`;
  async function saveSlug() {
    if (!normalized || normalized.length < 3) { toast.error(t("profile.slugTooShort")); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { slug: normalized });
      setUser(data); toast.success(t("profile.slugSaved")); setEditing(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  }
  return (
    <>
      <div className="flex items-center gap-2 bg-[#FAF9F6] border border-stone-200 rounded-full pl-4 pr-2 py-1.5">
        <span className="text-xs text-stone-500 flex-shrink-0">{window.location.host}/dr/</span>
        {editing ? (
          <input value={slug} autoFocus onChange={(e) => setSlug(e.target.value)} data-testid="slug-input"
            className="flex-1 bg-transparent text-sm outline-none px-1 border-b border-[#D4A373]" />
        ) : (
          <span className="text-sm text-stone-900 flex-1 truncate select-all font-medium" data-testid="portal-link-value">{user?.slug || "..."}</span>
        )}
        {editing ? (
          <>
            <button type="button" onClick={saveSlug} disabled={saving} data-testid="save-slug"
              className="px-3 py-1 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-50">{saving ? "…" : t("common.save")}</button>
            <button type="button" onClick={() => { setSlug(user?.slug || ""); setEditing(false); }}
              className="px-2 py-1 text-stone-500 hover:text-stone-900 text-xs">×</button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEditing(true)} data-testid="edit-slug"
              className="text-xs text-[#B07D4F] hover:underline">{t("profile.editSlug")}</button>
            <button type="button" onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin}/reservation/${user?.slug}`); toast.success(t("common.linkCopied")); }}
              data-testid="copy-portal-link" className="text-stone-500 hover:text-stone-900 p-1"><Copy className="w-4 h-4" /></button>
            <a href={`${process.env.REACT_APP_PUBLIC_BASE_URL || ""}/reservation/${user?.slug}`} target="_blank" rel="noreferrer" data-testid="open-portal-link"
              className="text-stone-500 hover:text-stone-900 p-1"><ExternalLink className="w-4 h-4" /></a>
          </>
        )}
      </div>
      {!editing && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="portal-join-link-row">
          <span className="text-[10px] uppercase tracking-widest text-stone-400">{t("profile.joinLinkLabel")}:</span>
          <span className="text-xs text-stone-700 font-mono flex-1 min-w-0 truncate select-all">{window.location.host}/patient/{user?.slug || "..."}</span>
          <button type="button" onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin}/patient/${user?.slug}`); toast.success(t("common.linkCopied")); }}
            data-testid="copy-join-link"
            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#B07D4F] hover:text-[#8a5a2b] px-2 py-1 border border-[#D4A373]/40 rounded-full">
            <Copy className="w-3 h-3" /> {t("profile.copyJoinLink")}
          </button>
        </div>
      )}
      {editing && (
        <p className="text-xs text-stone-400 mt-2 pl-2">
          {t("profile.slugPreview")}: <span className="font-mono text-stone-600">{previewUrl}</span>
        </p>
      )}
    </>
  );
}

function ScheduleEditor({ user, setUser, t }) {
  const [wh, setWh] = useState(DEFAULT_WH());
  const [slotDuration, setSlotDuration] = useState(30);
  const [savingSched, setSavingSched] = useState(false);
  useEffect(() => {
    const existing = user?.working_hours || {};
    const initial = DEFAULT_WH();
    for (const d of DAYS) if (existing[d.k]) initial[d.k] = { ...initial[d.k], ...existing[d.k] };
    setWh(initial);
    setSlotDuration(user?.slot_duration || 30);
  }, [user]);
  async function saveSchedule() {
    setSavingSched(true);
    try {
      const { data } = await api.put("/auth/profile", { working_hours: wh, slot_duration: slotDuration });
      setUser(data); toast.success(t("common.saved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingSched(false); }
  }
  return (
    <>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#8FA08B]/20 flex items-center justify-center flex-shrink-0">
          <CalendarClock className="w-4 h-4 text-[#8FA08B]" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-heading text-lg font-light">{t("profile.scheduleTitle")}</div>
          <div className="text-xs text-stone-500 mt-0.5">{t("profile.scheduleHint")}</div>
        </div>
        <div className="ml-auto">
          <label className="text-[10px] uppercase tracking-[0.2em] text-stone-500 block mb-1">{t("profile.slotDuration")}</label>
          <select value={slotDuration} onChange={(e) => setSlotDuration(Number(e.target.value))} data-testid="slot-duration"
            className="px-3 py-1.5 bg-[#FAF9F6] border border-stone-200 rounded-full text-sm">
            {[15, 20, 30].map((d) => <option key={d} value={d}>{d} min</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-2">
        {DAYS.map((d) => {
          const day = wh[d.k];
          return (
            <div key={d.k} className="flex flex-wrap items-center gap-3 p-3 bg-[#FAF9F6] rounded-xl" data-testid={`sched-${d.k}`}>
              <label className="flex items-center gap-2 min-w-[80px]">
                <input type="checkbox" checked={day.enabled} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, enabled: e.target.checked } })}
                  data-testid={`sched-${d.k}-enabled`} className="accent-[#8FA08B]" />
                <span className="uppercase text-xs tracking-widest text-stone-700">{d.l}</span>
              </label>
              {day.enabled && (
                <>
                  <div className="flex items-center gap-1 text-sm">
                    <input type="time" value={day.start} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, start: e.target.value } })}
                      className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                    <span className="text-stone-400">–</span>
                    <input type="time" value={day.end} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, end: e.target.value } })}
                      className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                  </div>
                  <div className="flex items-center gap-1 text-xs text-stone-500">
                    <span>{t("profile.break")}:</span>
                    <input type="time" value={day.break_start || ""} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, break_start: e.target.value } })}
                      className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                    <span className="text-stone-400">–</span>
                    <input type="time" value={day.break_end || ""} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, break_end: e.target.value } })}
                      className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" onClick={saveSchedule} disabled={savingSched} data-testid="save-schedule"
        className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#8FA08B] text-white hover:opacity-90 disabled:opacity-50">
        <Save className="w-4 h-4" /> {savingSched ? t("common.loading") : t("profile.saveSchedule")}
      </button>
    </>
  );
}

function BlockedDatesEditor({ t, compact = false }) {
  const [items, setItems] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/blocked-dates"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  async function add() {
    if (!start) { toast.error(t("blockedDates.pickDate")); return; }
    setSaving(true);
    try {
      if (end && end !== start) {
        await api.post("/blocked-dates/range", { start_date: start, end_date: end, reason });
      } else {
        await api.post("/blocked-dates", { date: start, reason });
      }
      toast.success(t("blockedDates.added"));
      setStart(""); setEnd(""); setReason("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  }
  async function del(id) {
    if (!window.confirm(t("blockedDates.confirmDelete"))) return;
    await api.delete(`/blocked-dates/${id}`);
    load();
  }
  return (
    <>
      <div className="flex items-start gap-3 mb-3">
        <div className={`${compact ? "w-8 h-8" : "w-10 h-10"} rounded-full bg-red-50 flex items-center justify-center flex-shrink-0`}>
          <CalendarOff className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-red-500`} strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className={`font-heading ${compact ? "text-base" : "text-lg"} font-light`}>{t("blockedDates.title")}</div>
          <div className={`${compact ? "text-[11px]" : "text-xs"} text-stone-500 mt-0.5`}>{t("blockedDates.subtitle")}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{t("blockedDates.from")}</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="blocked-start"
            className="w-full px-2.5 py-1.5 bg-[#FAF9F6] border border-stone-200 rounded-lg text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{t("blockedDates.to")}</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} min={start} data-testid="blocked-end"
            className="w-full px-2.5 py-1.5 bg-[#FAF9F6] border border-stone-200 rounded-lg text-xs" />
        </div>
      </div>
      <div className="flex gap-2 mb-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("blockedDates.reasonPh")}
          data-testid="blocked-reason"
          className="flex-1 px-2.5 py-1.5 bg-[#FAF9F6] border border-stone-200 rounded-lg text-xs" />
        <button onClick={add} disabled={saving || !start} data-testid="blocked-add"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 text-xs disabled:opacity-50 flex-shrink-0">
          <Plus className="w-3 h-3" /> {t("blockedDates.add")}
        </button>
      </div>
      <div className="mt-3">
        {items.length === 0 ? (
          <p className="text-[11px] text-stone-400 italic">{t("blockedDates.empty")}</p>
        ) : (
          <ul className={`space-y-1 ${compact ? "max-h-32" : "max-h-56"} overflow-y-auto pr-1`}>
            {items.map((b) => (
              <li key={b.id} data-testid={`blocked-item-${b.id}`}
                className="flex items-center justify-between text-xs bg-[#FAF9F6] rounded-lg px-2.5 py-1.5">
                <span className="min-w-0 truncate">
                  <span className="tabular-nums font-medium">{new Date(b.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</span>
                  {b.reason && <span className="text-stone-500 ml-1.5 italic">· {b.reason}</span>}
                </span>
                <button onClick={() => del(b.id)} data-testid={`blocked-del-${b.id}`}
                  className="text-stone-400 hover:text-red-500 flex-shrink-0 ml-2"><Trash2 className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

export default function PortalControls() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  if (!user) return null;
  return (
    <div className="space-y-6" data-testid="portal-controls">
      {/* Row 1: compact — Link público + Días bloqueados (vacaciones) */}
      <div className="grid lg:grid-cols-2 gap-6">
        {user.slug !== undefined && (
          <div className="bg-white border border-[#D4A373]/40 rounded-2xl p-5" data-testid="portal-link-card">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-3.5 h-3.5 text-[#B07D4F]" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-base font-light">{t("profile.publicLinkTitle")}</div>
                <div className="text-[11px] text-stone-500 mt-0.5 truncate">{t("profile.publicLinkHint")}</div>
              </div>
            </div>
            <SlugEditor user={user} setUser={setUser} t={t} />
          </div>
        )}
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="blocked-card">
          <BlockedDatesEditor t={t} compact />
        </div>
      </div>

      {/* Row 2: main — Calendario general + Semanas específicas, uno al lado del otro */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="schedule-card">
          <ScheduleEditor user={user} setUser={setUser} t={t} />
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="week-overrides-card">
          <WeekOverridesEditor user={user} t={t} />
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────
function mondayOf(dateStr) {
  // Returns YYYY-MM-DD of the Monday for the ISO week containing dateStr
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  const wd = (d.getDay() + 6) % 7; // Mon=0 ... Sun=6
  d.setDate(d.getDate() - wd);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtWeekRange(mon, locale = "it-IT") {
  if (!mon) return "";
  const sun = addDays(mon, 6);
  const dm = new Date(mon + "T00:00:00");
  const ds = new Date(sun + "T00:00:00");
  const opts = { day: "2-digit", month: "short" };
  const optsFull = { day: "2-digit", month: "short", year: "numeric" };
  return `${dm.toLocaleDateString(locale, opts)} – ${ds.toLocaleDateString(locale, optsFull)}`;
}

function WeekOverridesEditor({ user, t }) {
  const [list, setList] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [wh, setWh] = useState(DEFAULT_WH());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const generalWH = () => {
    const existing = user?.working_hours || {};
    const init = DEFAULT_WH();
    for (const d of DAYS) if (existing[d.k]) init[d.k] = { ...init[d.k], ...existing[d.k] };
    return init;
  };

  const load = async () => {
    try { const { data } = await api.get("/week-overrides"); setList(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  // When the picked week or the list changes, populate the editor.
  useEffect(() => {
    setLoading(true);
    const found = list.find((o) => o.week_start === selectedWeek);
    if (found) {
      const init = generalWH();
      for (const d of DAYS) if (found.working_hours?.[d.k]) init[d.k] = { ...init[d.k], ...found.working_hours[d.k] };
      setWh(init);
      setNote(found.note || "");
    } else {
      setWh(generalWH());
      setNote("");
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [selectedWeek, list, user]);

  async function saveWeek() {
    if (!selectedWeek) { toast.error(t("weekOverrides.pickWeek")); return; }
    setSaving(true);
    try {
      await api.post("/week-overrides", { week_start: selectedWeek, working_hours: wh, note });
      toast.success(t("weekOverrides.saved"));
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  }
  async function resetWeek() {
    const found = list.find((o) => o.week_start === selectedWeek);
    if (!found) { setWh(generalWH()); setNote(""); return; }
    if (!window.confirm(t("weekOverrides.confirmReset"))) return;
    await api.delete(`/week-overrides/${found.id}`);
    toast.success(t("weekOverrides.reset"));
    await load();
  }

  const hasOverride = !!list.find((o) => o.week_start === selectedWeek);

  return (
    <>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#D4A373]/20 flex items-center justify-center flex-shrink-0">
          <CalendarPlus className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-heading text-lg font-light">{t("weekOverrides.title")}</div>
          <div className="text-xs text-stone-500 mt-0.5">{t("weekOverrides.subtitle")}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">{t("weekOverrides.weekOf")}</label>
          <input type="date" value={selectedWeek}
            onChange={(e) => setSelectedWeek(mondayOf(e.target.value))}
            data-testid="week-override-picker"
            className="px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-lg text-sm" />
        </div>
        <div className="text-xs text-stone-600 pb-2.5">
          <span className="tabular-nums font-medium">{fmtWeekRange(selectedWeek)}</span>
          {hasOverride && <span className="ml-2 inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4A373]/25 text-[#8a5a2b]" data-testid="week-has-override">{t("weekOverrides.hasOverride")}</span>}
        </div>
      </div>

      {!loading && (
        <div className="space-y-2">
          {DAYS.map((d) => {
            const day = wh[d.k];
            return (
              <div key={d.k} className="flex flex-wrap items-center gap-3 p-3 bg-[#FAF9F6] rounded-xl" data-testid={`weekov-${d.k}`}>
                <label className="flex items-center gap-2 min-w-[80px]">
                  <input type="checkbox" checked={day.enabled} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, enabled: e.target.checked } })}
                    data-testid={`weekov-${d.k}-enabled`} className="accent-[#D4A373]" />
                  <span className="uppercase text-xs tracking-widest text-stone-700">{d.l}</span>
                </label>
                {day.enabled && (
                  <>
                    <div className="flex items-center gap-1 text-sm">
                      <input type="time" value={day.start} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, start: e.target.value } })}
                        className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={day.end} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, end: e.target.value } })}
                        className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                    </div>
                    <div className="flex items-center gap-1 text-xs text-stone-500">
                      <span>{t("profile.break")}:</span>
                      <input type="time" value={day.break_start || ""} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, break_start: e.target.value } })}
                        className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                      <span className="text-stone-400">–</span>
                      <input type="time" value={day.break_end || ""} onChange={(e) => setWh({ ...wh, [d.k]: { ...day, break_end: e.target.value } })}
                        className="px-2 py-1 bg-white border border-stone-200 rounded-md" />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("weekOverrides.notePh")}
        data-testid="week-override-note"
        className="w-full px-3 py-2 bg-[#FAF9F6] border border-stone-200 rounded-lg text-sm mt-3" />
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={saveWeek} disabled={saving} data-testid="week-override-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-700 text-sm disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? t("common.loading") : t("weekOverrides.saveWeek")}
        </button>
        {hasOverride && (
          <button onClick={resetWeek} data-testid="week-override-reset"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50 text-sm">
            <Trash2 className="w-3.5 h-3.5" /> {t("weekOverrides.resetToGeneral")}
          </button>
        )}
      </div>

      {list.length > 0 && (
        <div className="mt-5 pt-4 border-t border-stone-100">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">{t("weekOverrides.customWeeks")}</div>
          <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {list.map((o) => (
              <li key={o.id} data-testid={`weekov-item-${o.id}`}
                className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 cursor-pointer ${o.week_start === selectedWeek ? "bg-[#D4A373]/15" : "bg-[#FAF9F6] hover:bg-stone-100"}`}
                onClick={() => setSelectedWeek(o.week_start)}>
                <span className="tabular-nums">
                  {fmtWeekRange(o.week_start)}
                  {o.note && <span className="text-stone-500 ml-2 italic">· {o.note}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
